import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { CommentDatePipe } from '../../pipes/comment-date-pipe.pipe';
import { EmitterService } from '../../services/emitter.service';
import { ProjectService } from '../../services/project.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { FileTypeService } from '../../services/file-type.service';
import { StringUtilsService } from '../../services/string-utils.service';
import { isTimestamp } from '../../services/is-timestamp.util';

@Component({
  selector: 'mtm-comment-preview',
  templateUrl: './comment-preview.component.html',
  styleUrl: './comment-preview.component.scss',
  standalone: false,
})
export class CommentPreviewComponent implements OnChanges, OnDestroy {
  @Input() value: string;
  @Input() isEdited: boolean = false;
  @Input() dateEdited: string;
  @Input() textColor: any = 'inherit';
  @Input() fontSize: any = 'inherit';
  @Input() truncateText: boolean = false;
  @Input() isPreviewTitle: boolean = false;
  @Input() isAssetFilename: boolean = false;
  @Input() attachments: any;
  @Input() subSectionId: any;
  @Input() showDeleteFile: boolean = false;

  @Output() deleteFileCallback: EventEmitter<any> = new EventEmitter();

  project: any;
  taggedGroups: any = [];
  mentionedUsers: any = [];
  mentionConfig: any;
  isMentionDataOpened: boolean = false;
  isTruncated: boolean = false;
  displayValue: string;
  qlMentionElements: any;
  showFiles: boolean = false;

  constructor(
    private readonly elementRef: ElementRef,
    private readonly datePipe: CommentDatePipe,
    private readonly projectService: ProjectService,
    private readonly stringUtilsService: StringUtilsService,
    private readonly fileTypeService: FileTypeService,
    private readonly translatePipe: TranslatePipe
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.truncateText?.currentValue) {
      this.isTruncated = this.truncateText;
    }
    if (changes.value?.firstChange) {
      this.project = this.projectService.project;
      this.mentionConfig = {
        mentions: this.getMentionData(),
      };
      this.displayValue = this.getCommentBodyInPreview(this.value, this.isEdited);
      if (this.truncateText) {
        this.bindTruncateHandler();
      }
    } else if (changes.value && this.isPreviewTitle) {
      this.displayValue = this.getCommentBodyInPreview(this.value, this.isEdited);
    }
  }

  ngOnDestroy(): void {
    //** --- old element logic --- */
    this.taggedGroups = this.elementRef.nativeElement.querySelectorAll('.mentioned-group');
    for (const group of this.taggedGroups) {
      group.removeEventListener('click', this.taggedGroupClickHandler);
    }
    this.mentionedUsers = this.elementRef.nativeElement.querySelectorAll('.mentioned-user');
    for (const user of this.mentionedUsers) {
      user.removeEventListener('click', this.mentionedUserClickHandler);
    }
    //** --- old element logic --- */
    this.qlMentionElements = this.elementRef.nativeElement.querySelectorAll('.mention');
    for (const element of this.qlMentionElements) {
      if (element.dataset?.denotationChar === '@' && element.dataset?.id) {
        element.removeEventListener('click', this.mentionedUserClickHandler);
      } else if (element.dataset?.denotationChar === '#' && element.dataset?.id) {
        element.removeEventListener('click', this.taggedGroupClickHandler);
      }
    }
  }

  private bindTruncateHandler(): void {
    setTimeout(() => {
      const truncateButton: Element | null = this.elementRef.nativeElement.querySelector('.comment-view-nav');
      if (truncateButton) {
        truncateButton.addEventListener('click', (e: Event) => {
          e.stopImmediatePropagation();
          e.preventDefault();
          this.isTruncated = !this.isTruncated;
          this.displayValue = this.getCommentBodyInPreview(this.value, this.isEdited);
        });
      }
    }, 0);
  }

  private getMentionData(): any[] {
    return [
      {
        items: this.generateUserListData(),
        dropUp: true,
        labelKey: 'label',
        triggerChar: '@',
        allowSpace: false,
        mentionFilter: (query: any): any[] => {
          const allData: any[] = this.generateUserListData();
          return allData.filter((d: any) =>
            this.stringUtilsService
              .getNormalizedName(d.value)
              .includes(this.stringUtilsService.getNormalizedName(query))
          );
        },
        mentionSelect: (args: any): string => {
          this.isMentionDataOpened = false;
          return '@{{' + args.value + ':' + args.label + '}}';
        },
      },
      {
        items: this.generateConversationListData(),
        dropUp: true,
        labelKey: 'label',
        triggerChar: '#',
        allowSpace: false,
        mentionFilter: (query: any): any[] => {
          const allData: any[] = this.generateConversationListData();
          return allData.filter((d: any) =>
            this.stringUtilsService
              .getNormalizedName(d.value)
              .includes(this.stringUtilsService.getNormalizedName(query))
          );
        },
        mentionSelect: (args: any): string => {
          this.isMentionDataOpened = false;
          return '#{{' + args.value + ':' + args.label + '}}';
        },
      },
    ];
  }

  private generateUserListData(): any[] {
    return this.project?.participants?.map((participant: any) => ({
      value: participant?.username,
      label: participant?.fullName,
      type: 'one_on_one',
    }));
  }

  private generateConversationListData(): any[] {
    const conversations: any[] = this.project?.conversations?.filter(
      (conversation: any) => conversation?.type !== 'ONE_TO_ONE'
    );
    return conversations?.map((conversation: any) => {
      return {
        value: conversation?.id,
        label: conversation?.title,
        type: 'group',
        users: conversation?.users,
      };
    });
  }

  private getCommentBodyInPreview(comment: string, isEdited?: boolean): string | null {
    if (!comment) {
      return null;
    }
    const el: HTMLDivElement = document.createElement('div');
    el.innerHTML = comment;
    if (!el.innerText.trim()) {
      return null;
    }
    comment = comment.replace(/(?:\r\n|\r|\n)/g, '<br>');
    comment = this.parseToReadOnlyChatMessage(comment || '');
    if (isEdited) {
      if (this.isTruncated) {
        if (comment.length > 100) {
          comment =
            comment.substring(0, 100) +
            '<span>... <a class="comment-view-nav">' +
            this.translatePipe.transform('viewMore') +
            '</a></span>';
        } else {
          comment =
            comment +
            `<span class="tooltipped comment-has-edited ms-1" title="${this.datePipe.transform(this.dateEdited)}">(edited)</span>`;
        }
      } else {
        comment =
          comment +
          `<span class="tooltipped comment-has-edited ms-1" title="${this.datePipe.transform(this.dateEdited)}">(edited)</span>`;
      }
    }
    return this.isAssetFilename ? this.getDisplay(comment) : comment;
  }

  private getDisplay(name: any): string {
    if (!name) {
      return '';
    }
    const ext: string = name.split('.').length > 1 ? name.split('.').pop() : '';
    const nameArr: string[] = name.split('_');
    const isTime: boolean = isTimestamp(nameArr.pop().split('.')[0]);
    let savedName: string = '';
    if (isTime) {
      savedName = nameArr.join('_');
    } else {
      const nameParts: string[] = name.split('.');
      savedName = nameParts.length > 1 ? nameParts.slice(0, -1).join('.') : name;
    }

    return savedName.replace(/[^a-zA-Z0-9-] /g, '_') + (ext && ext !== 'undefined' ? '.' + ext : '');
  }

  private parseToReadOnlyChatMessage(message: string): string {
    if (!message) {
      return '';
    }
    const mentioned: string[] = message.match(/(@{{.*?}})/g) || [];
    if (mentioned.length) {
      for (const mentionItem of mentioned) {
        const data: string[] = mentionItem
          .replace(/[{()}]/g, '')
          .replace('@', '')
          .split(':');
        const [userEmail, userLabel] = data;
        const elementClass: string = this.mentionConfig.mentions[0].items.some((item: any) => item.value === userEmail)
          ? 'mentioned-user'
          : 'mentioned-user user-not-visible';
        message = message.replace(
          mentionItem,
          `\uFEFF<span data-id="${userEmail}" data-label="${userLabel}" contenteditable="false" class="${elementClass} c-mtm-brand">@${userLabel}</span>\uFEFF`
        );
      }
    }
    const tagging: string[] = message.match(/(#{{.*?}})/g) || [];
    if (tagging.length) {
      for (const tag of tagging) {
        const data: string[] = tag
          .replace(/[{()}]/g, '')
          .replace('#', '')
          .split(':');
        const [groupId, groupLabel] = data;
        const elementClass: string = this.mentionConfig.mentions[1].items.some((item: any) => item.value === groupId)
          ? 'mentioned-group'
          : 'mentioned-group group-not-visible';
        message = message.replace(
          tag,
          `\uFEFF<span data-id="${groupId}" data-label="${groupLabel}" contenteditable="false" class="${elementClass} c-mtm-brand">#${groupLabel}</span>\uFEFF`
        );
      }
    }
    this.bindElementClick();
    return message;
  }

  private bindElementClick(): void {
    setTimeout(() => {
      //** --- old element logic --- */
      this.taggedGroups = this.elementRef.nativeElement.querySelectorAll('.mentioned-group');
      for (const group of this.taggedGroups) {
        group.addEventListener('click', this.taggedGroupClickHandler);
      }
      this.mentionedUsers = this.elementRef.nativeElement.querySelectorAll('.mentioned-user');
      for (const user of this.mentionedUsers) {
        user.addEventListener('click', this.mentionedUserClickHandler);
      }
      //** --- old element logic --- */
      this.qlMentionElements = this.elementRef.nativeElement.querySelectorAll('.mention');
      for (const element of this.qlMentionElements) {
        if (element.dataset?.denotationChar === '@' && element.dataset?.id) {
          element.addEventListener('click', this.mentionedUserClickHandler);
        } else if (element.dataset?.denotationChar === '#' && element.dataset?.id) {
          element.addEventListener('click', this.taggedGroupClickHandler);
        }
      }
    }, 0);
  }

  private mentionedUserClickHandler(e: any): void {
    e.stopImmediatePropagation();
    const target: HTMLElement = e.target as HTMLElement;
    EmitterService.get('OPEN_CHAT').emit({
      type: 'one_on_one',
      username: target.dataset.id,
    });
  }

  private taggedGroupClickHandler(e: any): void {
    e.stopImmediatePropagation();
    const target: HTMLElement = e.target as HTMLElement;
    EmitterService.get('OPEN_CHAT').emit({
      type: 'group',
      groupId: target.dataset.id,
    });
  }

  public deleteFile(attachment: any): void {
    this.deleteFileCallback.emit(attachment);
  }
}
